import { api } from '../../../lib/api';
import type {
  Agente,
  BaileysStatus,
  ChipWhatsApp,
  ClienteResumo,
  Contato,
  Conversa,
  EstatisticasAgente,
  EventoChamado,
  Etiqueta,
  HorarioDia,
  Inbox,
  Mensagem,
  PerfilInbox,
} from './tipos';

/**
 * Texto da recusa "outra atendente esta com este contato" (409 do `POST /api/conversas`,
 * 28/09/2026), ou `null` se o erro for outro. Pedido do Ruan: essa recusa sai em POPUP — o
 * toast some em 4s e ela precisa saber com QUEM o cliente esta.
 */
export function recusaComOutraAtendente(e: unknown): string | null {
  const corpo = (e as { corpo?: { codigo?: string } } | null)?.corpo;
  return corpo?.codigo === 'com_outra_atendente' ? ((e as Error).message || null) : null;
}

/**
 * Um fly guardado ([DA-707]) — a COMBINACAO de ate 3 produtos, nunca a imagem: a arte se refaz
 * no envio, entao o preco sai sempre o de hoje.
 */
export interface FlySalvo {
  id: number;
  nome: string;
  autor: string | null;
  criado_em: string;
  /** Os mesmos campos que a busca de produtos entrega — clicar repoe a escolha na tela. */
  produtos: Array<{
    id: number;
    codigo_interno: string | null;
    descricao: string;
    preco_pix: string;
    foto_url: string;
    grupo_nome: string | null;
    tem_pagina: boolean;
    estoque: string;
  }>;
  /** Quantos produtos daquele fly sairam do ar (inativos, sem foto ou sem preco) — [DA-468]. */
  faltando: number;
}

interface ListaConversasParams {
  inbox?: Inbox | '';
  status?: 'todas' | 'atribuidas' | 'sem_atribuicao' | 'arquivadas';
  busca?: string;
  busca_modo?: 'contato' | 'mensagens';
  mostrar_arquivadas?: boolean;
  /** Filtro por etiqueta — aplicado no banco, antes do corte (15/10/2026). */
  etiqueta_id?: number;
  /** Filtros da barra aplicados no banco, antes do corte (15/10/2026). */
  filtro?: 'em_aberto' | 'nao_lidas' | 'grupos';
  limit?: number;
}

export const atendimentoApi = {
  // ----- Conversas -----
  listarConversas(params: ListaConversasParams = {}) {
    return api.get<Conversa[]>('/api/conversas', { query: params as Record<string, string | number | boolean> });
  },
  obterConversa(id: number) {
    return api.get<Conversa>(`/api/conversas/${id}`);
  },
  // Total de mensagens nao-lidas por inbox, pra bolinha nas abas (DA-164). Ex: { atendimento: 5, compras: 2 }
  contadoresNaoLidas() {
    return api.get<Partial<Record<Inbox, number>>>('/api/conversas/contadores');
  },
  // Chamados "Em Aberto" (atribuidos e ainda nao encerrados) por inbox, pro contador do
  // filtro "Em Aberto" (Onda Encerramento de Chamado, Fase 1). Ex: { atendimento: 3 }
  contadorEmAberto() {
    return api.get<Partial<Record<Inbox, number>>>('/api/conversas/contador-em-aberto');
  },
  /**
   * `atribuir: false` acha/cria a conversa SEM dar dono a ela ([DA-707]) — ver a rota.
   * ⚠️ Pode recusar com 409 `com_outra_atendente` (contato em andamento com outra atendente):
   * quem chama mostra POPUP, nao toast — use `recusaComOutraAtendente` no `catch`.
   */
  novaConversa(body: { telefone?: string; jid?: string; inbox: Inbox; nome?: string; atribuir?: boolean }) {
    return api.post<Conversa>('/api/conversas', body);
  },
  // FLYS GUARDADOS ([DA-707]) — a combinacao de ate 3 produtos que a loja quer repetir. Lista
  // compartilhada; apagar so' quem salvou (ou admin). ⚠️ `faltando` diz quantos produtos daquele
  // fly sairam do ar desde que ele foi guardado.
  listarFlysSalvos() {
    return api.get<FlySalvo[]>('/api/flys-salvos');
  },
  salvarFly(body: { nome: string; produto_ids: number[] }) {
    return api.post<{ id: number }>('/api/flys-salvos', body);
  },
  /** Corrige um fly guardado — nome e/ou produtos. So' o autor (ou admin), como o apagar. */
  editarFlySalvo(id: number, body: { nome?: string; produto_ids?: number[] }) {
    return api.put<{ id: number }>(`/api/flys-salvos/${id}`, body);
  },
  apagarFlySalvo(id: number) {
    return api.delete<{ ok: true }>(`/api/flys-salvos/${id}`);
  },
  atualizarConversa(id: number, body: Partial<Pick<Conversa, 'usuario_atribuido_id' | 'arquivada' | 'fixada' | 'silenciada' | 'mensagem_fixada_id'>>) {
    return api.put<Conversa>(`/api/conversas/${id}`, body);
  },
  // Assinatura automatica do usuario LOGADO (vale nas duas caixas) — gear do rodape.
  definirAssinaturaAtiva(ativa: boolean) {
    return api.put<{ ok: true; assinatura_ativa: boolean }>('/api/auth/me/assinatura', { assinatura_ativa: ativa });
  },
  // Rodizio de chamados do usuario LOGADO — gear do rodape. Desligar devolve pra fila os
  // chamados que ele nunca respondeu (`devolvidos`); os que ja respondeu ficam com ele.
  definirRodizio(recebe: boolean) {
    return api.put<{ ok: true; recebe_chamados: boolean; devolvidos: number }>('/api/agentes/me/rodizio', { recebe_chamados: recebe });
  },
  marcarLida(id: number) {
    return api.post<{ ok: true }>(`/api/conversas/${id}/marcar-lida`);
  },
  marcarNaoLida(id: number) {
    return api.post<{ ok: true; nao_lidas: number }>(`/api/conversas/${id}/marcar-nao-lida`);
  },
  atualizarStatusChamado(id: number, status: string) {
    return api.patch<{ ok: true; status: string }>(`/api/conversas/${id}/status`, { status });
  },
  listarPerdidos() {
    return api.get<{ qtd: number; itens: Array<{ id: number; nome: string | null; telefone: string | null; jid: string; foto_url: string | null; atendente_nome: string | null; status_em: string }> }>(
      '/api/conversas/perdidos',
    );
  },
  // Tira um chamado perdido do aviso (nao arquiva nem apaga a conversa).
  dispensarPerdido(id: number) {
    return api.post<{ ok: true }>(`/api/conversas/${id}/dispensar-perdido`);
  },
  // Chamados PARADOS agora (DA-113): encaminhado pra atendente unica sem resposta alem do limite.
  listarParados() {
    return api.get<{ qtd: number; itens: Array<{ id: number; nome: string | null; telefone: string | null; jid: string; foto_url: string | null; atendente_nome: string | null; status_em: string; min_parado: number }> }>(
      '/api/conversas/parados',
    );
  },
  // Chamados congelados esquecidos ha +16h (RDS 1.19/Fase 3): ja viraram advertencia de ponto.
  listarChamadosEsquecidos() {
    return api.get<{ qtd: number; itens: Array<{ id: number; nome: string | null; telefone: string | null; jid: string; foto_url: string | null; atendente_nome: string | null; congelada_em: string; horas: number }> }>(
      '/api/conversas/chamados-esquecidos',
    );
  },
  // Saude do canal de saida (DA-503): quantas mensagens do ERP ficaram sem recibo de entrega
  // na janela. Alarme de CANAL, nao de mensagem — o porque esta na rota.
  saudeEntrega() {
    return api.get<{
      sem_confirmacao: number; total: number; contatos: number; desde: string | null; pct: number;
      janela_h: number; limite_min: number;
    }>('/api/conversas/entrega-sem-confirmacao');
  },
  // Painel "Mensagens nao entregues" (DA-504): TODAS as sem recibo no periodo, nas duas inboxes,
  // sem portao de taxa — ferramenta que o operador abre, nao alarme. Ver a rota pro porque.
  mensagensSemConfirmacao(dias: number, incluirCientes = false) {
    return api.get<{
      dias: number;
      truncado: boolean;
      // Quantas o "ciente" escondeu no periodo — a ponte entre a lista curta e a faixa do topo.
      cientes: number;
      canal: { sem_confirmacao: number; total: number; pct: number; janela_h: number };
      itens: Array<{
        id: number; conversa_id: number; inbox: string; tipo: string; preview: string;
        quando: string; idade_s: number; nome: string | null; telefone: string | null;
        foto_url: string | null; enviado_por: string | null;
        ciente_em: string | null; ciente_motivo: string | null; ciente_por: string | null;
      }>;
    }>(`/api/conversas/mensagens-sem-confirmacao?dias=${dias}${incluirCientes ? '&cientes=1' : ''}`);
  },
  // "Ja cuidei disso" (DA-505): tira do painel acima. `ciente: false` devolve pra lista.
  marcarEntregaCiente(ids: number[], ciente = true) {
    return api.post<{ ok: true; afetadas: number }>('/api/conversas/mensagens-ciente', { ids, ciente });
  },
  // Manda a MESMA mensagem de novo na mesma conversa. 409 quando o recibo chegou nesse meio
  // tempo — nesse caso o backend recusa e diz o estado atual, em vez de duplicar no cliente.
  reenviarMensagem(id: number) {
    return api.post<{ ok: true; mensagem_id: number }>(`/api/mensagens/${id}/reenviar`);
  },
  excluirConversa(id: number) {
    return api.delete<{ ok: true }>(`/api/conversas/${id}`);
  },
  // Eventos de encaminhamento do chamado (pra exibir na propria conversa).
  listarEventosChamado(conversaId: number) {
    return api.get<EventoChamado[]>(`/api/conversas/${conversaId}/eventos`);
  },

  // ----- Mensagens -----
  listarMensagens(conversaId: number, opts: { after?: number; before?: number; limit?: number } = {}) {
    return api.get<Mensagem[]>(`/api/conversas/${conversaId}/mensagens`, { query: opts as Record<string, string | number> });
  },
  enviarTexto(conversaId: number, body: { texto: string; mensagem_citada_id?: number | null }) {
    return api.post<Mensagem>(`/api/conversas/${conversaId}/mensagens`, body);
  },
  enviarMidia(conversaId: number, body: { tipo: 'image' | 'video' | 'document' | 'audio'; base64: string; nome: string; mime: string; texto?: string; mensagem_citada_id?: number | null }) {
    return api.post<Mensagem>(`/api/conversas/${conversaId}/mensagens/midia`, body);
  },
  enviarAudio(conversaId: number, body: { base64: string; mime: string; mensagem_citada_id?: number | null }) {
    return api.post<Mensagem>(`/api/conversas/${conversaId}/mensagens/audio`, body);
  },
  enviarContato(conversaId: number, body: { contato_nome: string; contato_telefone: string }) {
    return api.post<Mensagem>(`/api/conversas/${conversaId}/mensagens/contato`, body);
  },
  enviarLocalizacao(conversaId: number, body: { latitude: number; longitude: number; nome?: string; endereco?: string }) {
    return api.post<Mensagem>(`/api/conversas/${conversaId}/mensagens/localizacao`, body);
  },
  // Um envio, duas origens (DA-682): produtos escolhidos na hora OU uma promocao pronta.
  // A tela tem um botao so', entao manda um dos dois — o servidor recusa os dois juntos.
  enviarFly(conversaId: number, corpo: { produto_ids: number[] } | { promocao_id: number }) {
    return api.post<Mensagem>(`/api/conversas/${conversaId}/enviar-fly`, corpo);
  },
  // Desenha a arte e devolve, SEM mandar nada. Mesma funcao do envio no servidor — uma
  // aproximacao feita no front mentiria, e o operador aprovaria o que nao vai sair.
  flyPrevia(conversaId: number, corpo: { produto_ids: number[] } | { promocao_id: number }) {
    return api.post<{
      imagem: string;
      itens: Array<{ id: number; codigo_interno: string | null; descricao: string; preco_pix: string }>;
      tem_link: boolean;
    }>(`/api/conversas/${conversaId}/fly-previa`, corpo);
  },
  promocoesParaEnvio(conversaId: number) {
    return api.get<Array<{
      id: number;
      sigla: string;
      grupo_nome: string;
      // Casa a etiqueta do contato (`lista_ar`) com o grupo da promocao — e' o que poe
      // a promocao do assunto do cliente no topo da coluna.
      do_assunto: boolean;
      qtd_produtos: number;
      com_estoque: number;
      produtos: Array<{ descricao: string; foto_url: string | null }>;
      enviavel: boolean;
      motivo_bloqueio: string | null;
    }>>(`/api/conversas/${conversaId}/promocoes-para-envio`);
  },
  enviarPix(conversaId: number) {
    return api.post<Mensagem & { aviso?: string | null }>(`/api/conversas/${conversaId}/enviar-pix`);
  },
  // Rota PROPRIA do chat, autenticada (DA-681). Ate 07/09/2026 isto chamava a
  // `/api/public/catalogo` — a vitrine —, que so' devolve os 129 produtos publicados;
  // agora alcanca os 1.496 ativos com foto e preco. A vitrine segue com o filtro dela.
  buscarProdutosParaEnvio(q: string) {
    return api.get<Array<{
      id: number;
      codigo_interno: string | null;
      descricao: string;
      preco_pix: string;
      foto_url: string;
      grupo_nome: string | null;
      tem_pagina: boolean;
    }>>(
      '/api/conversas/produtos-para-envio',
      { query: { q } as Record<string, string> },
    );
  },
  // Mesma rota, `modo=texto` — a campanha "Somente Texto" (RDS chamados 1.30). ⚠️ SEM exigir
  // foto (por isso `foto_url` pode vir nula) e COM `tem_descricao_comercial`: a tela recusa no
  // CLIQUE quem nao tem, sem esconder o produto da busca (decisao dele).
  buscarProdutosParaEnvioTexto(q: string) {
    return api.get<Array<{
      id: number;
      codigo_interno: string | null;
      descricao: string;
      preco_pix: string;
      foto_url: string | null;
      grupo_nome: string | null;
      tem_pagina: boolean;
      tem_descricao_comercial: boolean;
      estoque: string;
    }>>(
      '/api/conversas/produtos-para-envio',
      { query: { q, modo: 'texto' } as Record<string, string> },
    );
  },
  editarMensagem(id: number, texto: string) {
    return api.put<Mensagem>(`/api/mensagens/${id}`, { texto });
  },
  excluirMensagem(id: number, opts?: { paraTodos?: boolean }) {
    const qs = opts?.paraTodos ? '?todos=1' : '';
    return api.delete<{ ok: true; remoto?: 'ok' | 'falhou' | 'nao_aplicavel' }>(`/api/mensagens/${id}${qs}`);
  },
  reagirMensagem(id: number, emoji: string | null) {
    return api.post<Mensagem | { ok: true }>(`/api/mensagens/${id}/reagir`, { emoji });
  },
  transcreverMensagem(id: number) {
    return api.post<{ id: number; texto: string; modelo: string; em: string; cache?: boolean; ms?: number }>(
      `/api/mensagens/${id}/transcrever`,
    );
  },
  encaminharMensagem(id: number, conversaIds: number[]) {
    return api.post<{ resultados: Array<{ conversa_id: number; ok: boolean; mensagem_id?: number; erro?: string }> }>(
      `/api/mensagens/${id}/encaminhar`,
      { conversa_ids: conversaIds },
    );
  },

  // ----- Contatos -----
  listarContatos(params: { busca?: string; inbox?: Inbox | ''; etiqueta_id?: number; sem_grupo?: boolean; mostrar_ocultos?: boolean; limit?: number } = {}) {
    return api.get<Contato[]>('/api/contatos', { query: params as Record<string, string | number | boolean> });
  },
  obterContato(id: number) {
    return api.get<Contato>(`/api/contatos/${id}`);
  },
  atualizarContato(id: number, body: Partial<Pick<Contato, 'nome' | 'sobrenome' | 'telefone' | 'email' | 'foto_url' | 'foto_custom' | 'bloqueado' | 'oculto' | 'cliente_id'>>) {
    return api.put<Contato>(`/api/contatos/${id}`, body);
  },
  enviarFotoContato(id: number, body: { base64: string; mime: 'image/jpeg' | 'image/png' }) {
    return api.post<Contato>(`/api/contatos/${id}/foto`, body);
  },
  removerFotoContato(id: number) {
    return api.delete<Contato>(`/api/contatos/${id}/foto`);
  },
  // ----- Clientes (cadastro mod_arquivos) — usados pelo modal de salvar como cliente -----
  buscarClientes(busca: string) {
    return api.get<ClienteResumo[]>('/api/clientes', { query: { busca } });
  },
  obterCliente(id: number) {
    return api.get<ClienteResumo>(`/api/clientes/${id}`);
  },
  criarClienteDoContato(body: { nome: string; telefone?: string | null; whatsapp?: string | null; email?: string | null }) {
    return api.post<ClienteResumo>('/api/clientes', { ...body, tipo_pessoa: 'PF' });
  },
  atualizarEtiquetasContato(id: number, etiquetaIds: number[]) {
    return api.put<{ ok: true }>(`/api/contatos/${id}/etiquetas`, { etiqueta_ids: etiquetaIds });
  },
  ocultarContato(id: number) {
    return api.delete<{ ok: true }>(`/api/contatos/${id}`);
  },
  excluirContato(id: number) {
    return api.delete<{ ok: true }>(`/api/contatos/${id}/definitivo`);
  },

  // ----- Etiquetas -----
  listarEtiquetas() {
    return api.get<Etiqueta[]>('/api/etiquetas');
  },
  criarEtiqueta(body: { nome: string; cor?: string; apelido?: string | null; ocultar?: boolean; nao_cliente?: boolean; frequencia_fly_dias?: number }) {
    return api.post<Etiqueta>('/api/etiquetas', body);
  },
  atualizarEtiqueta(id: number, body: Partial<{ nome: string; cor: string; apelido: string | null; ocultar: boolean; nao_cliente: boolean; frequencia_fly_dias: number; escondida_gerenciar: boolean; exibir_gestao: boolean }>) {
    return api.put<Etiqueta>(`/api/etiquetas/${id}`, body);
  },
  excluirEtiqueta(id: number) {
    return api.delete<{ ok: true }>(`/api/etiquetas/${id}`);
  },

  // ----- Agentes -----
  listarAgentes() {
    return api.get<Agente[]>('/api/agentes');
  },
  listarAgentesDisponiveis(inbox: Inbox) {
    return api.get<Array<Pick<Agente, 'id' | 'nome' | 'foto'>>>('/api/agentes/disponiveis', { query: { inbox } });
  },
  atualizarAgente(id: number, body: Partial<Pick<Agente, 'departamentos' | 'ausente_temporario' | 'whatsapp_contact_id'>>) {
    return api.put<Agente>(`/api/agentes/${id}`, body);
  },

  // ----- Estatisticas -----
  estatisticasHoje(date?: string) {
    return api.get<EstatisticasAgente[]>('/api/agent-stats/today', date ? { query: { date } } : undefined);
  },
  estatisticasPeriodo(de: string, ate: string) {
    return api.get<EstatisticasAgente[]>('/api/agent-stats/summary', { query: { from: de, to: ate } });
  },
  contatosDoAgente(usuarioId: number, date: string) {
    return api.get<Array<{ id: number; nome: string; jid: string; telefone: string | null; foto_url: string | null; conversa_id: number; respondido: boolean; atendido_por: string | null; resposta_automatica: boolean; assumido_admin: boolean; horario: string | null }>>(
      `/api/agent-stats/contacts/${usuarioId}`,
      { query: { date } },
    );
  },
  vendasDoAgente(usuarioId: number, date: string) {
    return api.get<Array<{ id: number; numero: string; total: string; comprador: string | null; cliente_nome: string | null; horario: string }>>(
      `/api/agent-stats/vendas/${usuarioId}`,
      { query: { date } },
    );
  },
  // Contatos unicos do dia (numeros distintos que mandaram msg) — registro persistido.
  resumoDia(date: string) {
    return api.get<{ contatos_unicos: number }>('/api/agent-stats/resumo-dia', { query: { date } });
  },
  resumoPeriodo(de: string, ate: string) {
    return api.get<{ contatos_unicos: number }>('/api/agent-stats/resumo-dia', { query: { from: de, to: ate } });
  },
  // Lista dos contatos do dia/periodo (expande o badge).
  contatosDoDia(date: string) {
    return api.get<Array<{ id: number; nome: string | null; telefone: string | null; jid: string | null; foto_url: string | null; horario: string | null }>>(
      '/api/agent-stats/contatos-dia', { query: { date } },
    );
  },
  contatosDoDiaPeriodo(de: string, ate: string) {
    return api.get<Array<{ id: number; nome: string | null; telefone: string | null; jid: string | null; foto_url: string | null; horario: string | null }>>(
      '/api/agent-stats/contatos-dia', { query: { from: de, to: ate } },
    );
  },

  // ----- Horario de atendimento -----
  listarHorario() {
    return api.get<HorarioDia[]>('/api/horario-atendimento');
  },
  atualizarDiaHorario(dia: number, body: Partial<Pick<HorarioDia, 'abre_em' | 'fecha_em' | 'ativo' | 'mensagem_auto_reply'>>) {
    return api.put<HorarioDia>(`/api/horario-atendimento/${dia}`, body);
  },

  // ----- App settings -----
  obterSetting(chave: string) {
    return api.get<{ chave: string; valor: string | null }>(`/api/app-settings/${chave}`);
  },
  atualizarSetting(chave: string, valor: string | null) {
    return api.put<{ ok: true }>(`/api/app-settings/${chave}`, { valor });
  },

  // ----- Perfil da inbox (WA Business) -----
  obterPerfilInbox(inbox: Inbox) {
    return api.get<PerfilInbox>(`/api/inbox-profile`, { query: { inbox } });
  },
  atualizarPerfilInbox(inbox: Inbox, body: Partial<Omit<PerfilInbox, 'inbox' | 'profile_picture_url'>>) {
    return api.put<PerfilInbox>(`/api/inbox-profile`, { inbox, ...body });
  },
  enviarFotoPerfilInbox(body: { base64: string; mime: 'image/jpeg' | 'image/png' }) {
    return api.post<{ ok: boolean; handle: string }>(`/api/inbox-profile/foto`, body);
  },

  // ----- Celulares de WhatsApp (admin) — DA-510 -----
  //
  // O parametro e' o nome da INSTANCIA (o chip), nao mais a caixa: desde que a
  // loja pode ter mais de um celular na mesma caixa, "atendimento" nao identifica
  // um aparelho. Segue aceitando nome de caixa pra nao quebrar chamada antiga.
  baileysStatus(chip: string) {
    return api.get<BaileysStatus>(`/api/baileys/${chip}/status`);
  },
  baileysQr(chip: string) {
    return api.get<{ qr: string | null; inbox: string }>(`/api/baileys/${chip}/qr`);
  },
  baileysReiniciar(chip: string) {
    return api.post<{ ok: true }>(`/api/baileys/${chip}/restart`);
  },
  baileysDesconectar(chip: string) {
    return api.post<{ ok: true }>(`/api/baileys/${chip}/disconnect`);
  },
  chipsListar() {
    return api.get<{ chips: ChipWhatsApp[] }>('/api/baileys/chips');
  },
  chipCriar(body: { rotulo: string; inbox: Inbox }) {
    return api.post<{ ok: true; instancia: string }>('/api/baileys/chips', body);
  },
  chipLigar(chip: string, ativo: boolean) {
    return api.post<{ ok: true; ativo: boolean }>(`/api/baileys/chips/${chip}/ativo`, { ativo });
  },
  chipDefinirRemetente(chip: string) {
    return api.post<{ ok: true; instancia: string; inbox: string }>(`/api/baileys/chips/${chip}/principal`);
  },
  chipRemover(chip: string) {
    return api.delete<{ ok: true }>(`/api/baileys/chips/${chip}`);
  },

  // ----- Link preview -----
  linkPreview(url: string) {
    return api.get<{ preview: LinkPreview | null; erro: string | null }>('/api/link-preview', { query: { url } });
  },
};

export interface LinkPreview {
  url: string;
  titulo: string | null;
  descricao: string | null;
  imagem: string | null;
  /** Icone do site, so' quando nao ha `imagem` (miniatura a esquerda, como o WhatsApp). */
  icone: string | null;
  sitio: string | null;
}
